import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Upload } from "lucide-react";

const IMAGE_FIELDS = [
  { key: "hero_image", label: "Imagem principal do Hero" },
  { key: "promo_image", label: "Imagem do Banner de Ofertas" },
  { key: "store_image", label: "Imagem da Loja Física" },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    type: "percent",
    value: 10,
  });

  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  const load = () => {
    api.get("/settings").then((r) => setSettings(r.data || {}));
    api.get("/admin/coupons").then((r) => setCoupons(r.data || []));
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) =>
    setSettings((s) => ({
      ...s,
      [k]: v,
    }));

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings((s) => ({ ...s, ...(data || {}) }));
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadImage = async (field, file) => {
    if (!file) return;

    setUploading(field);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/admin/upload", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      set(field, data.url);
      toast.success("Imagem enviada. Clique em Salvar para confirmar.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading("");
      if (fileRefs.current[field]) {
        fileRefs.current[field].value = "";
      }
    }
  };

  const addCoupon = async () => {
    if (!newCoupon.code) return;

    try {
      await api.post("/admin/coupons", {
        ...newCoupon,
        value: parseFloat(newCoupon.value),
      });

      toast.success("Cupom criado");

      setNewCoupon({
        code: "",
        type: "percent",
        value: 10,
      });

      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const delCoupon = async (id) => {
    try {
      await api.delete(`/admin/coupons/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminLayout>
      <h1
        className="text-2xl font-bold mb-6"
        style={{ fontFamily: "Outfit" }}
      >
        Configurações da loja
      </h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border rounded-xl p-5 space-y-4">
          <h2 className="font-bold">Informações da loja</h2>

          <div>
            <Label>Nome da loja</Label>
            <Input
              value={settings.store_name || ""}
              onChange={(e) => set("store_name", e.target.value)}
            />
          </div>

          <div>
            <Label>Endereço</Label>
            <Input
              value={settings.address || ""}
              onChange={(e) => set("address", e.target.value)}
            />
          </div>

          <div>
            <Label>WhatsApp</Label>
            <Input
              value={settings.whatsapp || ""}
              onChange={(e) => set("whatsapp", e.target.value)}
            />
          </div>

          <div>
            <Label>Instagram</Label>
            <Input
              value={settings.instagram || ""}
              onChange={(e) => set("instagram", e.target.value)}
            />
          </div>
          <div>
  <Label>Facebook</Label>
  <Input
    value={settings.facebook || ""}
    onChange={(e) => set("facebook", e.target.value)}
  />
</div>

          <div>
            <Label>E-mail comercial</Label>
            <Input
              value={settings.email || ""}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>

          <div className="border-t pt-5 mt-5">
            <h3 className="font-bold mb-3">Textos da página inicial</h3>
            <div className="space-y-4">
              {[
                ["hero_eyebrow", "Chamada acima do Hero"],
                ["hero_headline", "Título do Hero"],
                ["hero_support", "Subtítulo do Hero"],
                ["hero_badge", "Selo do Hero"],
                ["hero_badge_support", "Texto ao lado do selo"],
                ["hero_card_label", "Rótulo do destaque do Hero"],
                ["hero_card_title", "Título do destaque do Hero"],
                ["promo_eyebrow", "Chamada do banner de ofertas"],
                ["promo_title", "Título do banner de ofertas"],
                ["promo_banner", "Texto do banner promocional"],
                ["store_section_label", "Rótulo da seção da loja"],
                ["store_section_title", "Título da seção da loja"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input value={settings[key] || ""} onChange={(e) => set(key, e.target.value)} />
                </div>
              ))}
              <div>
                <Label>Descrição da seção da loja</Label>
                <textarea
                  value={settings.store_section_description || ""}
                  onChange={(e) => set("store_section_description", e.target.value)}
                  className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-5 mt-5">
            <h3 className="font-bold mb-1">Imagens da página inicial</h3>
            <p className="text-xs text-neutral-500 mb-4">
              Envie uma nova imagem e depois clique em Salvar.
            </p>

            <div className="space-y-5">
              {IMAGE_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label>{label}</Label>

                  {settings[key] && (
                    <div className="w-full max-w-sm aspect-video rounded-lg overflow-hidden border bg-neutral-100">
                      <img
                        src={settings[key]}
                        alt={label}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <input
                    ref={(el) => {
                      fileRefs.current[key] = el;
                    }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) =>
                      uploadImage(key, e.target.files?.[0])
                    }
                  />

                  <Button
                    type="button"
                    variant="outline"
                    disabled={!!uploading}
                    onClick={() => fileRefs.current[key]?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading === key
                      ? "Enviando..."
                      : "Enviar do computador"}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Button
            data-testid="save-settings"
            onClick={save}
            className="bg-[#C91F1F] hover:bg-[#9F1515]"
          >
            Salvar
          </Button>
        </section>

        <section className="bg-white border rounded-xl p-5 space-y-3">
          <h2 className="font-bold">Cupons de desconto</h2>

          <div className="grid grid-cols-3 gap-2">
            <Input
              placeholder="CÓDIGO"
              value={newCoupon.code}
              onChange={(e) =>
                setNewCoupon({
                  ...newCoupon,
                  code: e.target.value.toUpperCase(),
                })
              }
            />

            <select
              className="border rounded-md px-3"
              value={newCoupon.type}
              onChange={(e) =>
                setNewCoupon({
                  ...newCoupon,
                  type: e.target.value,
                })
              }
            >
              <option value="percent">Percentual (%)</option>
              <option value="fixed">Fixo (R$)</option>
            </select>

            <Input
              type="number"
              value={newCoupon.value}
              onChange={(e) =>
                setNewCoupon({
                  ...newCoupon,
                  value: e.target.value,
                })
              }
            />
          </div>

          <Button
            onClick={addCoupon}
            variant="outline"
            className="w-full"
          >
            Criar cupom
          </Button>

          <ul className="space-y-2 mt-3">
            {coupons.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between border rounded-lg p-2"
              >
                <div>
                  <span className="font-mono font-bold">{c.code}</span>
                  <span className="text-xs text-neutral-500 ml-2">
                    {c.type === "percent"
                      ? `${c.value}%`
                      : `R$ ${c.value}`}
                  </span>
                </div>

                <button
                  onClick={() => delCoupon(c.id)}
                  className="text-neutral-400 hover:text-[#C91F1F]"
                  aria-label="Excluir cupom"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <strong>Mercado Pago:</strong> gateway em modo MOCK. Para
            ativar produção, adicione MP_ACCESS_TOKEN nas variáveis de
            ambiente do backend e implemente a integração real na camada
            de pagamento.
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}